<template>
    <div class="booking-datetime">
        <h4>Date / Time</h4>
        <div class="form-group">
            <label :for="`trip-date-${tripId}`">{{ tripLabel }} Date</label>
            <div v-if="dateEditable">
            <Calendar
                :id="`trip-date-${tripId}`"
                v-model="day.date"
                :disabled="!hasDates"
                :minDate="minDate"
                :maxDate="maxDate"
                :disabledDates="disabledDates"
                :selectOtherMonths="true"
                dateFormat="dd/mm/yy"
            />
            </div>
            <div v-else>
                <strong>{{ dateString }}</strong>
            </div>
            <div v-if="errors?.booking_day?.date?.length > 0" class="booking-error">
                <p>There are some problems with this field.</p>
                <ul>
                    <li v-for="error, index in errors.booking_day.date" :key="index">{{ error }}</li>
                </ul>
            </div>
        </div>
        <div class="form-group">
            <label :for="`trip-time-${tripId}`">{{ tripLabel }} Time</label>
            <select v-model="selectedTimeRaw" class="form-control" :id="`trip-time-${tripId}`" :disabled="times.length == 0">
                <option 
                    v-for="time, index in times"
                    :key="index" 
                    :value="time.time"
                >
                    {{ time.time }}
                </option>
            </select>
            <div v-if="errors?.route?.time?.length > 0" class="booking-error">
                <p>There are some problems with this field.</p>
                <ul>
                    <li v-for="error, index in errors.route.time" :key="index">{{ error }}</li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import isValid from 'date-fns/isValid';
import dateMax from 'date-fns/max';
import dateMin from 'date-fns/min';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import isSameDay from 'date-fns/isSameDay';
import format from 'date-fns/format';
import Calendar from 'primevue/calendar';

export default {
    components: { Calendar },
    props: {
        bookingDay: Object,
        tripRoute: Object,
        tripOptions: Array,
        tripId: Number,
        setTime: String,
        bookingService: Object,
        errors: Object,
        dateEditable: {
            type: Boolean,
            default: true,
        },
        tripLabel: {
            type: String,
            default: 'Trip',
        },
    },
    data() {
        return {
            selectedTimeRaw: null
        }
    },
    computed: {
        day: {
            get() { 
                return this.bookingDay;
            },
            set(value) {
                this.$emit('update:bookingDay', value);
            }
        },

        route: {
            get() { 
                return this.tripRoute;
            },
            set(value) {
                this.$emit('update:tripRoute', value);
            }
        },

        dates() {
            return this.tripOptions.map(option => option.date);
        },

        minDate() {
            if (this.dates.length === 0)
                return null;

            return dateMin(this.dates);
        },

        maxDate() {
            if (this.dates.length === 0)
                return null;

            return dateMax(this.dates);
        },

        hasDates() {
            if (!isValid(this.minDate) || !isValid(this.maxDate))
                return false;
            
            if (this.dates?.length == 0)
                return false;

            return true;
        },

        disabledDates() {
            if (!isValid(this.minDate) || !isValid(this.maxDate))
                return [];

            let dates = eachDayOfInterval({
                start: this.minDate,
                end: this.maxDate
            });

            return dates.filter(intervalDate => {
                return !(this.dates.find(listDate => {
                    return isSameDay(intervalDate, listDate);
                }) !== undefined);
            })
        },

        selectedOption() {
            return this.tripOptions.find(option => isSameDay(option.date, this.day.date));
        },

        times() {
            let times = this?.selectedOption?.times;
            if (times === undefined) 
                return [];
            return times;
        },

        selectedTime() {
            let foundTime = this.times.find(time => time.time === this.selectedTimeRaw);

            if (foundTime === undefined) {
                return {
                    route_id: null,
                    time_id: null,
                    time: null,
                    is_day: null
                };
            }
            
            return foundTime;
        },

        dateString() {
            return format(this.day.date, 'dd/MM/yyyy');
        }
    },
    watch: {
        selectedTime() {
            if (this.selectedTime) {
                this.route = this.selectedTime;
            }
        }
    },
    created() {
        if (this.setTime) {
            this.selectedTimeRaw = this.setTime;
        }
    }
}
</script>

<style lang="scss">
.booking-datetime {
    h4 {
        font-size: 1.2rem;
    }
}
</style>